import { yupResolver } from "@hookform/resolvers/yup";
import { Save } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";

import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@krea/shared/design-system/Form";
import Checkbox from "@krea/shared/design-system/fields/Checkbox";
import { Label } from "@krea/shared/design-system/Label";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import type { IUserSettings } from "@/features/lender-companies/api/types";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";

const schema = yup.object().shape({
  receiveFactoringEmails: yup.boolean().required(),
  receiveCompanyLoanEmails: yup.boolean().required(),
  receiveRealEstateEmails: yup.boolean().required(),
  receiveLeasingEmails: yup.boolean().required(),
  receiveEmailsOnlyOnSelectedAndLater: yup.boolean().required(),
});

type NotificationSettingsFormValues = yup.InferType<typeof schema>;

interface INotificationsSectionProps {
  settings: IUserSettings;
  onUpdateUserSettings: (data: NotificationSettingsFormValues) => void;
}

export const NotificationsSection = ({
  settings,
  onUpdateUserSettings,
}: INotificationsSectionProps) => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const form = useForm<NotificationSettingsFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      receiveFactoringEmails: false,
      receiveCompanyLoanEmails: false,
      receiveRealEstateEmails: false,
      receiveLeasingEmails: false,
      receiveEmailsOnlyOnSelectedAndLater: false,
    },
  });
  const { setValue, handleSubmit } = form;

  const watch = useWatch({
    control: form.control,
  });

  const isAllEmailChecked = useMemo(() => {
    return Object.values(watch).every(Boolean);
  }, [watch]);

  useEffect(() => {
    const initialValues = {
      receiveFactoringEmails: settings.receiveFactoringEmails,
      receiveCompanyLoanEmails: settings.receiveCompanyLoanEmails,
      receiveRealEstateEmails: settings.receiveRealEstateEmails,
      receiveLeasingEmails: settings.receiveLeasingEmails,
      receiveEmailsOnlyOnSelectedAndLater:
        settings.receiveEmailsOnlyOnSelectedAndLater,
    };
    form.reset(initialValues);
  }, [settings, form]);

  const setAllEmail = (checked: boolean) => {
    setValue("receiveFactoringEmails", checked);
    setValue("receiveCompanyLoanEmails", checked);
    setValue("receiveRealEstateEmails", checked);
    setValue("receiveLeasingEmails", checked);
    setValue("receiveEmailsOnlyOnSelectedAndLater", checked);
  };

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      onUpdateUserSettings(data);
      toast.success("Notification settings updated successfully");
    })();
  }, [handleSubmit, onUpdateUserSettings]);

  return (
    <Card>
      <Form {...form}>
        <CardContent className="space-y-6">
          <h1 className="text-xl font-bold">Notification Settings</h1>

          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-primary-150 p-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center">
                  <Checkbox
                    checked={isAllEmailChecked}
                    onChange={(checked) => setAllEmail(checked)}
                  />
                  <span className="font-semibold text-gray-900">
                    All Email Notifications
                  </span>
                </Label>
                <span className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-500">
                  Master Control
                </span>
              </div>

              <Separator className="my-3" />

              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="receiveFactoringEmails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        </FormControl>
                        <span className="block font-medium text-gray-800">
                          Receive factoring emails
                        </span>
                      </FormLabel>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="receiveCompanyLoanEmails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        </FormControl>
                        <span className="block font-medium text-gray-800">
                          Receive company loan emails
                        </span>
                      </FormLabel>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="receiveRealEstateEmails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        </FormControl>
                        <span className="block font-medium text-gray-800">
                          Receive real estate emails
                        </span>
                      </FormLabel>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="receiveLeasingEmails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        </FormControl>
                        <span className="block font-medium text-gray-800">
                          Receive leasing emails
                        </span>
                      </FormLabel>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="receiveEmailsOnlyOnSelectedAndLater"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onChange={(checked) => field.onChange(checked)}
                          />
                        </FormControl>
                        <span className="block font-medium text-gray-800">
                          Receive emails only on selected and later
                        </span>
                      </FormLabel>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          <Separator />
          <div className="flex justify-end">
            <Button type="button" onClick={onSubmit}>
              <Save className="mr-2 h-4 w-4" />
              Save Notification Settings
            </Button>
          </div>
        </CardContent>
      </Form>
    </Card>
  );
};
